import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CheckCircle2, 
  Circle, 
  Sparkles, 
  UserRound, 
  MessageCircle, 
  Target, 
  PenLine,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CHECKLIST_ITEMS = [
  {
    id: 'welcome_seen',
    label: 'Welcome to ClarityPath',
    description: 'Learn what ClarityPath can do for you',
    icon: Sparkles,
    action: null, // Auto-completed
  },
  {
    id: 'profile_setup',
    label: 'Set up your profile',
    description: 'Tell us a bit about yourself',
    icon: UserRound,
    action: '/settings',
  },
  {
    id: 'first_chat',
    label: 'Meet CLaiR',
    description: 'Have your first conversation with your AI coach',
    icon: MessageCircle,
    action: '/coach',
  },
  {
    id: 'first_goal',
    label: 'Create your first goal',
    description: 'Set something meaningful to work toward',
    icon: Target,
    action: '/goals',
  },
  {
    id: 'first_journal',
    label: 'Write your first journal entry',
    description: 'Reflect on where you are today',
    icon: PenLine,
    action: '/journal',
  },
];

export const GettingStartedChecklist = ({ steps, onDismiss, isCollapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const [fading, setFading] = useState(false);
  
  const completedCount = Object.values(steps || {}).filter(Boolean).length;
  const totalCount = CHECKLIST_ITEMS.length;
  const progress = Math.round((completedCount / totalCount) * 100);
  const allCompleted = completedCount === totalCount;

  // Auto-dismiss after 5 seconds when all steps completed
  useEffect(() => {
    if (allCompleted) {
      const fadeTimer = setTimeout(() => setFading(true), 4000);
      const dismissTimer = setTimeout(() => onDismiss(), 5000);
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(dismissTimer);
      };
    }
  }, [allCompleted, onDismiss]);

  const handleItemClick = (item) => {
    if (item.action && !steps?.[item.id]) {
      navigate(item.action);
    }
  };

  if (allCompleted) {
    return (
      <Card className={`p-6 rounded-2xl border-border bg-gradient-to-br from-accent/10 to-primary/10 transition-opacity duration-1000 ${fading ? 'opacity-0' : 'opacity-100'}`} data-testid="onboarding-complete">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
              <CheckCircle2 className="text-accent" size={24} />
            </div>
            <div>
              <h3 className="font-medium">You're all set!</h3>
              <p className="text-sm text-muted-foreground">You've completed the getting started guide</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onDismiss}
            className="text-muted-foreground hover:text-foreground"
            data-testid="dismiss-checklist"
          >
            <X size={18} />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl border-border overflow-hidden" data-testid="getting-started-checklist">
      {/* Header - Always visible */}
      <div 
        className="p-4 bg-gradient-to-r from-primary/5 to-accent/5 cursor-pointer"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="text-primary" size={20} />
            </div>
            <div>
              <h3 className="font-medium">Getting Started</h3>
              <p className="text-sm text-muted-foreground">{completedCount} of {totalCount} completed</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Progress bar */}
            <div className="hidden sm:block w-24 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            {isCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
          </div>
        </div>
      </div>

      {/* Checklist Items - Collapsible */}
      {!isCollapsed && (
        <div className="p-4 pt-2 space-y-2">
          {CHECKLIST_ITEMS.map((item) => {
            const Icon = item.icon;
            const isCompleted = steps?.[item.id];
            const isClickable = item.action && !isCompleted;
            
            return (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                  isCompleted 
                    ? 'bg-accent/5' 
                    : isClickable 
                      ? 'bg-muted/30 hover:bg-muted/50 cursor-pointer' 
                      : 'bg-muted/30'
                }`}
                data-testid={`checklist-item-${item.id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCompleted ? 'bg-accent/20' : 'bg-muted'
                }`}>
                  {isCompleted ? (
                    <CheckCircle2 className="text-accent" size={18} />
                  ) : (
                    <Icon className="text-muted-foreground" size={16} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isCompleted ? 'text-muted-foreground line-through' : ''}`}>
                    {item.label}
                  </p>
                  {!isCompleted && (
                    <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                  )}
                </div>
                {isClickable && (
                  <span className="text-xs text-primary font-medium">Start →</span>
                )}
              </div>
            );
          })}
          
          {/* Dismiss link */}
          <div className="pt-2 text-center">
            <button
              onClick={onDismiss}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="dismiss-getting-started"
            >
              I don't need this guide
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};
